const PREDICTIVE_DEBOUNCE_MS = 250;
const PREDICTIVE_LOADING_DELAY_MS = 150;
const PREDICTIVE_MIN_CHARS = 2;
const PREDICTIVE_LIMIT = 6;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.status = this.querySelector('[data-status]');
    if (!this.input || !this.results) return;

    this.activeIndex = -1;
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.input.addEventListener('focus', () => this.reopen());
    this.results.addEventListener('click', (event) => this.onResultsClick(event));
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    });
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.controller?.abort();
    clearTimeout(this.debounceTimer);
    clearTimeout(this.loadingTimer);
  }

  get options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  get isOpen() {
    return !this.results.hidden;
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const term = this.input.value.trim();
    if (term.length < PREDICTIVE_MIN_CHARS) {
      this.controller?.abort();
      this.reset();
      return;
    }
    this.debounceTimer = setTimeout(() => this.search(term), PREDICTIVE_DEBOUNCE_MS);
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.isOpen) {
          this.reopen();
          return;
        }
        const count = this.options.length;
        if (!count) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.setActive((this.activeIndex + step + count) % count);
        break;
      }
      case 'Enter': {
        const active = this.options[this.activeIndex];
        if (!this.isOpen || !active) return;
        event.preventDefault();
        active.click();
        break;
      }
      case 'Escape': {
        if (this.isOpen) {
          this.close();
        } else if (this.input.value) {
          this.input.value = '';
          this.reset();
        } else {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.input.focus({ preventScroll: true });
        break;
      }
      default:
    }
  }

  onResultsClick(event) {
    if (!event.target.closest('[data-retry]')) return;
    this.input.focus({ preventScroll: true });
    this.search(this.input.value.trim());
  }

  async search(term) {
    if (term.length < PREDICTIVE_MIN_CHARS) return;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.lastTerm = term;
    this.setBusy(true);

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page',
      'resources[limit]': String(PREDICTIVE_LIMIT),
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed with status ${response.status}`);
      const markup = parseHTML(await response.text()).querySelector('[data-predictive-results]');
      if (controller !== this.controller) return;
      if (!markup) throw new Error('Predictive search returned no markup');
      this.renderResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.renderError();
    } finally {
      if (controller === this.controller) this.setBusy(false);
    }
  }

  setBusy(isBusy) {
    clearTimeout(this.loadingTimer);
    this.results.setAttribute('aria-busy', String(isBusy));
    if (isBusy) this.loadingTimer = setTimeout(() => this.renderLoading(), PREDICTIVE_LOADING_DELAY_MS);
  }

  cloneTemplate(name) {
    return this.querySelector(`template[data-template="${name}"]`)?.content.cloneNode(true) ?? null;
  }

  renderLoading() {
    const loading = this.cloneTemplate('loading');
    if (loading) this.show(loading);
  }

  renderResults(markup) {
    const prefix = this.input.id;
    markup.querySelectorAll('[id]').forEach((el) => {
      const scoped = `${prefix}-${el.id}`;
      markup.querySelectorAll(`[aria-labelledby="${el.id}"]`).forEach((ref) => ref.setAttribute('aria-labelledby', scoped));
      el.id = scoped;
    });
    markup.querySelectorAll('[role="option"]').forEach((option, index) => {
      option.id = `${prefix}-option-${index}`;
    });
    if (markup.dataset.resultCount === '0') this.renderSuggestions(markup.querySelector('[data-suggestions-slot]'));
    this.show(markup);
    this.announce(markup.dataset.announcement);
  }

  renderSuggestions(slot) {
    const suggestions = this.cloneTemplate('suggestions');
    if (slot && suggestions) slot.replaceChildren(suggestions);
  }

  renderError() {
    const error = this.cloneTemplate('error');
    if (error) this.show(error);
  }

  show(content) {
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.results.replaceChildren(content);
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  setActive(index) {
    const options = this.options;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    const active = options[index];
    this.activeIndex = index;
    if (!active) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  announce(message) {
    if (!this.status || !message) return;
    this.status.textContent = '';
    requestAnimationFrame(() => { this.status.textContent = message; });
  }

  reopen() {
    if (this.isOpen || !this.results.hasChildNodes()) return;
    if (this.input.value.trim() !== this.lastTerm) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;
    this.setActive(-1);
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  reset() {
    clearTimeout(this.loadingTimer);
    this.results.removeAttribute('aria-busy');
    this.close();
    this.results.replaceChildren();
    this.lastTerm = '';
    if (this.status) this.status.textContent = '';
  }
}
customElements.define('predictive-search', PredictiveSearch);
